import { ApolloError } from '@apollo/client';
import { ErrorCode } from '@dokucoach/common-types';
import { useTranslation } from 'react-i18next';
import { errorMessages } from '../utils/errorMessages';

interface ErrorHandler {
  getErrorMessage: (error: ApolloError) => string;
}

export const useErrorHandler = (): ErrorHandler => {
  const { t } = useTranslation();

  /**
   * Returns a proper translated error message according to the error object received from Apollo client
   * @param error: an ApolloError object
   */
  const getErrorMessage = (error: ApolloError): string => {
    /**
     * From Apollo docs:
     * These are errors encountered while attempting to communicate with your GraphQL server
     * - which means it cannot even reach our endpoints for some reason.
     */
    if (error.networkError) {
      console.log('Network Error:', error.networkError);
      return t('error_networkErrorGeneric');
    }

    /**
     * These are errors related to the server-side execution of a GraphQL operation.
     */
    if (error.graphQLErrors?.length) {
      /**
       * Take the first error (in most cases there will be only one), and check if its message
       * is a custom ErrorCode for which we have a custom error message.
       */
      const current = error.graphQLErrors[0];
      const code = current.message as ErrorCode;
      if (errorMessages.has(code)) {
        const translationKey = errorMessages.get(code);
        if (translationKey !== undefined) {
          return t(translationKey);
        }
      }

      /**
       * If not, check if it has a status code, and return a generic but status related message.
       */
      const statusCode = current.extensions?.exception?.status;
      if (statusCode && Number.isInteger(statusCode)) {
        switch (statusCode) {
          case 400:
            return t('error_badRequestErrorGeneric');
          case 401:
            return t('error_unauthorizedErrorGeneric');
          case 403:
            return t('error_forbiddenErrorGeneric');
          case 404:
            return t('error_notFoundErrorGeneric');
          default:
            break;
        }
      }
    }

    /**
     * If none of the above, fall back to a "fully generic" unknown error message.
     */
    if (error.message) {
      const code = error.message as ErrorCode;
      if (errorMessages.has(code)) {
        const translationKey = errorMessages.get(code);
        if (translationKey !== undefined) {
          return t(translationKey);
        }
      }
    }
    return t('error_unknownError');
  };

  return { getErrorMessage };
};
